/**
 * @Author: Nemo
 * @Description: 表单字段验证规则配置
 * @Date: 2024-11-30
 * @FilePath: /rhcn-checkout-web/src/common/config/field-config/rules.js
 */

import { getRule } from '@/plugins/validator/index';
import { identityNumberLabel } from '@/utils/tools/utils';

/**
 * 获取表单字段的规则列表
 * @param {Array} fields 字段配置数组
 * @param {string} country 国家代码（可选，用于身份证验证）
 * @returns {Object} 包含每个字段规则的对象
 */
export function getRuleList(fields, country) {
  const rules = {};

  if (!fields || !fields.length) {
    return rules;
  }

  fields.forEach(field => {
    const fieldRules = [];

    // 处理必填规则
    if (field.require === 'Y') {
      fieldRules.push('required');
    }

    // 处理特定字段规则
    switch (field.name) {
    case 'cardNum':
      fieldRules.push('cardNumber');
      break;
    case 'effectiveDate':
      fieldRules.push('monthYear');
      break;
    case 'cvv':
      fieldRules.push('cvv');
      break;
    // case 'cardHolderName':
    //   fieldRules.push('holderName');
    //   break;
    case 'email':
      fieldRules.push('email');
      break;
    case 'birthDate':
      fieldRules.push('birthDate');
      break;
    case 'issueDate':
      fieldRules.push('issueDate');
      break;
    case 'blikcode':
      fieldRules.push('blikcode');
      break;
    default:
      // 如果field.rules存在且包含规则列表，则添加这些规则
      if (field.rules && Array.isArray(field.rules)) {
        field.rules.forEach(rule => {
          if (typeof rule === 'string' && rule !== 'required') {
            fieldRules.push(rule);
          }
        });
      }
    }

    // 身份证号特殊处理
    if (field.name === 'identityNumber' && country) {
      if (field.require === 'Y') {
        fieldRules.push(['identityNumberRequired', country]);
      }
      fieldRules.push(['identityNumber', country]);
    }

    // 只有当有规则时才添加到结果中
    if (fieldRules.length > 0) {
      rules[field.name] = fieldRules;
    }
  });

  return rules;
}

/**
 * 将规则列表转换为表单验证所需的格式
 * @param {Object} ruleList 规则列表对象
 * @returns {Object} 格式化后的验证规则对象
 */
export function formatRules(ruleList) {
  const formattedRules = {};

  for (const field in ruleList) {
    if (Object.prototype.hasOwnProperty.call(ruleList, field)) {
      const fieldRules = ruleList[field];
      formattedRules[field] = fieldRules.map(rule => {
        if (Array.isArray(rule)) {
          const [ruleName, ...params] = rule;
          return {
            validator: (rule, value, callback) => {
              const ruleObj = getRule(ruleName);
              const isValid = ruleObj.validate(value, ...params);
              if (isValid) {
                callback();
              } else {
                const message = typeof ruleObj.message === 'function'
                  ? ruleObj.message({ t: (key, opts) => key }, { param1: params[0] })
                  : ruleObj.message;
                callback(new Error(message));
              }
            },
            trigger: 'blur',
          };
        } else if (typeof rule === 'string') {
          const ruleObj = getRule(rule);
          return {
            validator: (rule, value, callback) => {
              const isValid = ruleObj.validate(value);
              if (isValid) {
                callback();
              } else {
                const message = typeof ruleObj.message === 'function'
                  ? ruleObj.message({ t: (key, opts) => key })
                  : ruleObj.message;
                callback(new Error(message));
              }
            },
            trigger: 'blur',
          };
        }
        return rule;
      });
    }
  }

  return formattedRules;
}

/**
 * 获取身份证号标签
 * @param {string} country 国家代码
 * @returns {string} 标签文本
 */
export function getIdentityNumberLabel(country) {
  return identityNumberLabel[country] || 'document';
}

/**
 * 生成包含验证规则的完整表单配置
 * @param {Array} fields 字段配置数组
 * @param {string} country 国家代码
 * @returns {Array} 包含验证规则的字段配置数组
 */
export function generateFieldsWithRules(fields, country) {
  if (!fields || !fields.length) {
    return fields;
  }

  const ruleList = getRuleList(fields, country);

  return fields.map(field => {
    const newField = { ...field };
    if (ruleList[field.name]) {
      newField.validationRules = ruleList[field.name];
    }
    return newField;
  });
}
